import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, Clock, ClipboardCheck } from "lucide-react";

export default function AdminCierres() {
  const [cierres, setCierres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todos");

  useEffect(() => {
    fetchCierres();
  }, [filtro]);

  const fetchCierres = async () => {
    try {
      const params = filtro !== "todos" ? { estado: filtro } : {};
      const response = await api.get("/cierres", { params });
      setCierres(response.data);
    } catch (error) {
      console.error("Error fetching cierres:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAprobar = async (id) => {
    try {
      await api.put(`/cierres/${id}/aprobar`);
      toast.success("Cierre aprobado");
      fetchCierres();
    } catch (error) {
      toast.error("Error al aprobar cierre");
    }
  };

  const handleRechazar = async (id) => {
    try {
      await api.put(`/cierres/${id}/rechazar`);
      toast.success("Cierre rechazado");
      fetchCierres();
    } catch (error) {
      toast.error("Error al rechazar cierre");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-cierres">
      <div>
        <h1 className="font-display text-3xl text-torta-dark">Cierres de Caja</h1>
        <p className="text-torta-muted">Revisa y aprueba los cierres de los vendedores</p>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {["todos", "pendiente", "aprobado", "rechazado"].map((f) => (
          <Button
            key={f}
            variant={filtro === f ? "default" : "outline"}
            size="sm"
            onClick={() => setFiltro(f)}
            className={filtro === f ? "bg-torta-brown" : ""}
          >
            {f === "todos" ? "Todos" : f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* Cierres List */}
      <div className="space-y-4">
        {cierres.map((cierre) => (
          <div
            key={cierre.id}
            className="bg-white rounded-2xl p-6 shadow-sm"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-display text-xl text-torta-dark">
                  {cierre.vendedor_nombre}
                </h3>
                <p className="text-torta-muted text-sm">{formatDate(cierre.fecha)}</p>
              </div>
              <span
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  cierre.estado === "pendiente"
                    ? "bg-amber-100 text-amber-700"
                    : cierre.estado === "aprobado"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {cierre.estado === "pendiente" && <Clock className="w-4 h-4" />}
                {cierre.estado === "aprobado" && <Check className="w-4 h-4" />}
                {cierre.estado === "rechazado" && <X className="w-4 h-4" />}
                {cierre.estado}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-torta-cream rounded-xl p-3">
                <p className="text-xs text-torta-muted">Total Ventas</p>
                <p className="font-display text-lg text-torta-dark">
                  {formatCurrency(cierre.total_ventas)}
                </p>
              </div>
              <div className="bg-torta-cream rounded-xl p-3">
                <p className="text-xs text-torta-muted">Efectivo</p>
                <p className="font-display text-lg text-torta-dark">
                  {formatCurrency(cierre.total_efectivo)}
                </p>
              </div>
              <div className="bg-torta-cream rounded-xl p-3">
                <p className="text-xs text-torta-muted">Transferencia</p>
                <p className="font-display text-lg text-torta-dark">
                  {formatCurrency(cierre.total_transferencia)}
                </p>
              </div>
              <div className="bg-torta-cream rounded-xl p-3">
                <p className="text-xs text-torta-muted">Unidades</p>
                <p className="font-display text-lg text-torta-dark">
                  {cierre.unidades_vendidas} vendidas / {cierre.unidades_devueltas} devueltas
                </p>
              </div>
            </div>

            {cierre.notas && (
              <p className="text-sm text-torta-muted mb-4 bg-torta-sand/30 rounded-lg p-3">
                <strong>Notas:</strong> {cierre.notas}
              </p>
            )}

            {cierre.estado === "pendiente" && (
              <div className="flex gap-3 pt-4 border-t border-torta-sand">
                <Button
                  onClick={() => handleAprobar(cierre.id)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Aprobar
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleRechazar(cierre.id)}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Rechazar
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {cierres.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl">
          <ClipboardCheck className="w-12 h-12 text-torta-muted mx-auto mb-4" />
          <p className="text-torta-muted">No hay cierres {filtro !== "todos" ? filtro + "s" : ""}</p>
        </div>
      )}
    </div>
  );
}
